import React, { ChangeEvent, forwardRef } from 'react';
import { createStyles, makeStyles, TextField, TextFieldProps, Typography } from '@material-ui/core';
import { MultiRowInput, MultiRowInputProps } from '../MultiRowInput';

const useStyles = makeStyles(() =>
  createStyles({
    helperText: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  }),
);

export type CharacterLimitFields = {
  value?: string | undefined;
};

export type MultiRowCharacterLimitInputProps = Omit<MultiRowInputProps, 'children'> & {
  className?: string;
  InputProps?: TextFieldProps;
  value?: CharacterLimitFields;
  onChange?: (value: CharacterLimitFields) => void;
  onBlur?: () => void;
  onAdd?: () => void;
  index: number;
  characterLimit?: number;
  error?: string;
  disabled?: boolean;
  disableAdd?: boolean;
};

export const MultiRowCharacterLimitInput = forwardRef<HTMLDivElement, MultiRowCharacterLimitInputProps>(
  (
    {
      InputProps,
      className,
      value: valueProp = { value: '' },
      onChange = () => undefined,
      onBlur = () => undefined,
      onAdd = () => undefined,
      onRemove = () => undefined,
      index,
      error,
      characterLimit = 35,
      disabled = false,
      disableAdd = false,
      ...props
    }: MultiRowCharacterLimitInputProps,
    ref,
  ) => {
    const classes = useStyles();
    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...valueProp, value: event?.currentTarget?.value });
    };
    return (
      <MultiRowInput
        onAdd={onAdd}
        onRemove={onRemove}
        index={index}
        {...props}
        disableAdd={disableAdd}
        disabled={disabled}
      >
        <TextField
          ref={ref}
          className={className}
          fullWidth
          value={valueProp.value}
          onChange={handleChange}
          onBlur={onBlur}
          {...InputProps}
          inputProps={{ maxLength: characterLimit }}
          error={!!error}
          helperText={
            <span className={classes.helperText}>
              {error ? (
                <Typography data-testid="error" variant="caption">
                  {error}
                </Typography>
              ) : (
                <Typography data-testid="char-limit" variant="caption">
                  {characterLimit} Characters or less
                </Typography>
              )}
              <Typography data-testid="char-count" variant="caption">
                {valueProp?.value?.length || 0}/{characterLimit}
              </Typography>
            </span>
          }
          disabled={disabled}
        />
      </MultiRowInput>
    );
  },
);

MultiRowCharacterLimitInput.displayName = 'MultiRowCharacterLimitInput';
